/**
 * Calendar actions (DESIGN.md §18): one registry per target kind, used by the right-click menu on the grid, the "…"
 * menu of the event panel and the "New" menu of the command bar.
 * - Slot actions act on the selected free time (or the next free slot).
 * - Event actions follow Outlook's rules: attendees cannot edit the organizer's meeting (Delete = decline and
 *   remove), free/busy blocks and private items of others offer nothing but Open.
 */
import { defineActions, type ActionRegistry, type LabelRef } from '@platform/ui';
import {
  CalendarClockIcon,
  CalendarDaysIcon,
  CalendarPlusIcon,
  CheckIcon,
  ClipboardPasteIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CopyIcon,
  EyeIcon,
  FolderInputIcon,
  FolderOutputIcon,
  DownloadIcon,
  LinkIcon,
  RefreshCwIcon,
  UploadIcon,
  CopyPlusIcon,
  ForwardIcon,
  HelpCircleIcon,
  LockIcon,
  PaletteIcon,
  PencilIcon,
  PrinterIcon,
  RepeatIcon,
  Share2Icon,
  SquareArrowOutUpRightIcon,
  TagIcon,
  Trash2Icon,
  UserPlusIcon,
  UsersIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react';
import type { CalendarEvent, CalendarInfo } from '../api';
import type { ShownEvent } from '../calendar/events';
import { isBlock } from '../calendar/events';
import type { CalendarView } from '../calendar/format';

export interface SlotTarget {
  start: Date;
  end: Date;
  allDay: boolean;
}

export type NewKind = 'event' | 'meeting' | 'allDay' | 'recurring';

export interface SlotActionHandlers {
  create(slot: SlotTarget, kind: NewKind): void;
  paste(slot: SlotTarget): void;
  today(): void;
  /** Print the shown period. */
  print(): void;
  view(view: CalendarView): void;
}

const slotSurfaces = ['overflow', 'context'] as const;

export function createSlotActions(handlers: SlotActionHandlers, options: { canPaste: boolean }): ActionRegistry<SlotTarget> {
  const slot = (targets: readonly SlotTarget[]) => targets[0]!;
  const create = (id: NewKind, label: LabelRef, icon: LucideIcon) => ({
    id: `calendar.new.${id}`,
    label,
    icon,
    group: 'new',
    surfaces: slotSurfaces,
    run: ({ targets }: { targets: readonly SlotTarget[] }) => handlers.create(slot(targets), id),
  });
  const view = (id: CalendarView, label: LabelRef) => ({
    id: `calendar.view.${id}`,
    label,
    group: 'view',
    surfaces: slotSurfaces,
    run: () => handlers.view(id),
  });
  return defineActions<SlotTarget>([
    create('event', 'webmail.calendar.actions.newEvent', CalendarPlusIcon),
    create('meeting', 'webmail.calendar.actions.newMeeting', UsersIcon),
    create('allDay', 'webmail.calendar.actions.newAllDay', CalendarDaysIcon),
    create('recurring', 'webmail.calendar.actions.newRecurring', RepeatIcon),
    {
      id: 'calendar.paste',
      label: 'webmail.calendar.actions.paste',
      icon: ClipboardPasteIcon,
      group: 'edit',
      surfaces: slotSurfaces,
      visible: () => options.canPaste,
      run: ({ targets }) => handlers.paste(slot(targets)),
    },
    { id: 'calendar.today', label: 'webmail.calendar.today', icon: CalendarClockIcon, group: 'go', surfaces: slotSurfaces, run: () => handlers.today() },
    { id: 'calendar.printView', label: 'webmail.calendarUx.print.action', icon: PrinterIcon, group: 'go', surfaces: slotSurfaces, shortcut: 'calendar.print', run: () => handlers.print() },
    view('day', 'webmail.calendar.views.day'),
    view('workWeek', 'webmail.calendar.views.workWeek'),
    view('week', 'webmail.calendar.views.week'),
    view('month', 'webmail.calendar.views.month'),
    view('agenda', 'webmail.calendar.views.agenda'),
  ]);
}

export interface EventActionHandlers {
  open(item: ShownEvent): void;
  edit(item: ShownEvent): void;
  copy(item: ShownEvent): void;
  duplicate(item: ShownEvent): void;
  remove(item: ShownEvent): void;
  showAs(item: ShownEvent, value: CalendarEvent['showAs']): void;
  togglePrivate(item: ShownEvent): void;
  respond(item: ShownEvent, response: 'accept' | 'tentative' | 'decline'): void;
  /** Attendee: propose a new time; anyone in the meeting: forward; organizer: add attendees. */
  meeting(item: ShownEvent, kind: 'propose' | 'forward' | 'addAttendees'): void;
  /** Colour and categories of the event. */
  categorize(item: ShownEvent): void;
  /** Download the event as .ics. */
  saveIcs(item: ShownEvent): void;
}

const eventSurfaces = ['overflow', 'context'] as const;

/** Can the user change this item (own/editor calendar, not masked, not someone else's meeting)? */
export const editable = (item: ShownEvent): boolean => !item.event.readOnly && !item.event.masked && !isBlock(item.event) && (!item.event.isMeeting || item.event.isOrganizer);
/** Invitation the user can answer (own or fully owned calendar). */
export const answerable = (item: ShownEvent): boolean => item.event.isMeeting && !item.event.isOrganizer && !item.event.readOnly && !item.event.masked;

export function createEventActions(handlers: EventActionHandlers): ActionRegistry<ShownEvent> {
  const one = (targets: readonly ShownEvent[]) => targets[0]!;
  const showAs = (value: CalendarEvent['showAs'], label: LabelRef) => ({
    id: `calendar.showAs.${value}`,
    label,
    group: 'showAs',
    surfaces: eventSurfaces,
    // Attendees may change show-as of their copy, organizers of theirs.
    visible: ({ targets }: { targets: readonly ShownEvent[] }) => !one(targets).event.readOnly && !one(targets).event.masked && !isBlock(one(targets).event),
    enabled: ({ targets }: { targets: readonly ShownEvent[] }) => one(targets).event.showAs !== value,
    run: ({ targets }: { targets: readonly ShownEvent[] }) => handlers.showAs(one(targets), value),
  });
  const respond = (response: 'accept' | 'tentative' | 'decline', label: LabelRef, icon: LucideIcon) => ({
    id: `calendar.respond.${response}`,
    label,
    icon,
    group: 'respond',
    surfaces: eventSurfaces,
    visible: ({ targets }: { targets: readonly ShownEvent[] }) => answerable(one(targets)),
    run: ({ targets }: { targets: readonly ShownEvent[] }) => handlers.respond(one(targets), response),
  });
  return defineActions<ShownEvent>([
    { id: 'calendar.open', label: 'webmail.calendar.actions.open', icon: SquareArrowOutUpRightIcon, group: 'open', surfaces: eventSurfaces, defaultGesture: true, run: ({ targets }) => handlers.open(one(targets)) },
    { id: 'calendar.edit', label: 'webmail.calendar.edit', icon: PencilIcon, group: 'open', surfaces: eventSurfaces, visible: ({ targets }) => editable(one(targets)), run: ({ targets }) => handlers.edit(one(targets)) },
    respond('accept', 'webmail.calendar.accept', CheckIcon),
    respond('tentative', 'webmail.calendar.tentative', HelpCircleIcon),
    respond('decline', 'webmail.calendar.decline', XIcon),
    { id: 'calendar.copy', label: 'webmail.calendar.actions.copy', icon: CopyIcon, group: 'clipboard', surfaces: eventSurfaces, visible: ({ targets }) => !one(targets).event.masked && !isBlock(one(targets).event), run: ({ targets }) => handlers.copy(one(targets)) },
    { id: 'calendar.duplicate', label: 'webmail.calendar.actions.duplicate', icon: CopyPlusIcon, group: 'clipboard', surfaces: eventSurfaces, visible: ({ targets }) => !one(targets).event.masked && !isBlock(one(targets).event), run: ({ targets }) => handlers.duplicate(one(targets)) },
    showAs('busy', 'webmail.calendar.actions.showAsBusy'),
    showAs('tentative', 'webmail.calendar.actions.showAsTentative'),
    showAs('free', 'webmail.calendar.actions.showAsFree'),
    showAs('oof', 'webmail.calendar.actions.showAsAway'),
    showAs('workingElsewhere', 'webmail.calendar.actions.showAsElsewhere'),
    {
      id: 'calendar.private',
      labelFor: ({ targets }) => (one(targets).event.sensitivity === 'private' ? 'webmail.calendar.actions.notPrivate' : 'webmail.calendar.actions.private'),
      label: 'webmail.calendar.actions.private',
      icon: LockIcon,
      group: 'flags',
      surfaces: eventSurfaces,
      visible: ({ targets }) => editable(one(targets)),
      run: ({ targets }) => handlers.togglePrivate(one(targets)),
    },
    {
      id: 'calendar.categories',
      label: 'webmail.calendarUx.categorize.title',
      icon: TagIcon,
      group: 'flags',
      surfaces: eventSurfaces,
      visible: ({ targets }) => !one(targets).event.readOnly && !one(targets).event.masked && !isBlock(one(targets).event),
      run: ({ targets }) => handlers.categorize(one(targets)),
    },
    {
      id: 'calendar.proposeTime',
      label: 'webmail.calendarUx.propose.title',
      icon: CalendarClockIcon,
      group: 'respond',
      surfaces: eventSurfaces,
      visible: ({ targets }) => answerable(one(targets)),
      run: ({ targets }) => handlers.meeting(one(targets), 'propose'),
    },
    {
      id: 'calendar.addAttendees',
      label: 'webmail.calendarUx.people.addTitle',
      icon: UserPlusIcon,
      group: 'share',
      surfaces: eventSurfaces,
      visible: ({ targets }) => one(targets).event.isMeeting && editable(one(targets)) && one(targets).calendar.kind === 'own',
      run: ({ targets }) => handlers.meeting(one(targets), 'addAttendees'),
    },
    {
      id: 'calendar.forward',
      label: 'webmail.calendar.actions.forward',
      icon: ForwardIcon,
      group: 'share',
      surfaces: eventSurfaces,
      visible: ({ targets }) => one(targets).event.isMeeting && !one(targets).event.masked && one(targets).calendar.kind === 'own',
      run: ({ targets }) => handlers.meeting(one(targets), 'forward'),
    },
    {
      id: 'calendar.saveIcs',
      label: 'webmail.calendarUx.ics.saveEvent',
      icon: DownloadIcon,
      group: 'share',
      surfaces: eventSurfaces,
      visible: ({ targets }) => !one(targets).event.masked && !isBlock(one(targets).event),
      run: ({ targets }) => handlers.saveIcs(one(targets)),
    },
    {
      id: 'calendar.delete',
      labelFor: ({ targets }) => (one(targets).event.isMeeting && one(targets).event.isOrganizer ? 'webmail.calendar.cancelMeeting' : 'webmail.calendar.delete'),
      label: 'webmail.calendar.delete',
      icon: Trash2Icon,
      group: 'delete',
      danger: true,
      surfaces: eventSurfaces,
      visible: ({ targets }) => one(targets).event.canDelete && !one(targets).event.masked && !isBlock(one(targets).event),
      run: ({ targets }) => handlers.remove(one(targets)),
    },
  ]);
}

export interface CalendarListActionHandlers {
  share(calendar: CalendarInfo): void;
  edit(calendar: CalendarInfo): void;
  remove(calendar: CalendarInfo): void | Promise<void>;
  /** iCalendar: export/import (own), publish a link (own), download again (subscriptions). */
  exportIcs(calendar: CalendarInfo): void;
  importIcs(calendar: CalendarInfo): void;
  publish(calendar: CalendarInfo): void;
  refresh(calendar: CalendarInfo): void | Promise<void>;
  /** Navigation: order, groups, show only this one. */
  move(calendar: CalendarInfo, direction: -1 | 1): void;
  group(calendar: CalendarInfo): void;
  ungroup(calendar: CalendarInfo): void;
  showOnly(calendar: CalendarInfo): void;
}

export interface CalendarListOptions {
  canMove: (calendar: CalendarInfo, direction: -1 | 1) => boolean;
  grouped: (calendar: CalendarInfo) => boolean;
}

/** Additional own calendars are folders of the mailbox (rename, delete); the default one and added ones are not. */
export const isExtraCalendar = (calendar: Pick<CalendarInfo, 'id' | 'kind'>): boolean => calendar.kind === 'own' && calendar.id !== 'own';

/** Actions of a calendar in the rail ("…" button and right-click). */
export function createCalendarListActions(handlers: CalendarListActionHandlers, options: CalendarListOptions = { canMove: () => false, grouped: () => false }): ActionRegistry<CalendarInfo> {
  const one = (targets: readonly CalendarInfo[]) => targets[0]!;
  return defineActions<CalendarInfo>([
    { id: 'calendar.list.share', label: 'webmail.calendar.sharing.open', icon: Share2Icon, group: 'share', visible: ({ targets }) => one(targets).canShare, run: ({ targets }) => handlers.share(one(targets)) },
    {
      id: 'calendar.list.edit',
      labelFor: ({ targets }) => (isExtraCalendar(one(targets)) ? 'webmail.calendar.list.edit' : 'webmail.calendar.list.color'),
      label: 'webmail.calendar.list.color',
      icon: PaletteIcon,
      group: 'edit',
      run: ({ targets }) => handlers.edit(one(targets)),
    },
    { id: 'calendar.list.only', label: 'webmail.calendarUx.groups.only', icon: EyeIcon, group: 'view', run: ({ targets }) => handlers.showOnly(one(targets)) },
    { id: 'calendar.list.up', label: 'webmail.calendarUx.groups.up', icon: ArrowUpIcon, group: 'order', enabled: ({ targets }) => options.canMove(one(targets), -1), run: ({ targets }) => handlers.move(one(targets), -1) },
    { id: 'calendar.list.down', label: 'webmail.calendarUx.groups.down', icon: ArrowDownIcon, group: 'order', enabled: ({ targets }) => options.canMove(one(targets), 1), run: ({ targets }) => handlers.move(one(targets), 1) },
    { id: 'calendar.list.group', label: 'webmail.calendarUx.groups.moveTo', icon: FolderInputIcon, group: 'order', run: ({ targets }) => handlers.group(one(targets)) },
    { id: 'calendar.list.ungroup', label: 'webmail.calendarUx.groups.ungroup', icon: FolderOutputIcon, group: 'order', visible: ({ targets }) => options.grouped(one(targets)), run: ({ targets }) => handlers.ungroup(one(targets)) },
    { id: 'calendar.list.refresh', label: 'webmail.calendarUx.ics.refresh', icon: RefreshCwIcon, group: 'edit', visible: ({ targets }) => one(targets).kind === 'subscription', run: ({ targets }) => handlers.refresh(one(targets)) },
    { id: 'calendar.list.publish', label: 'webmail.calendarUx.ics.publishMenu', icon: LinkIcon, group: 'share', visible: ({ targets }) => one(targets).kind === 'own', run: ({ targets }) => handlers.publish(one(targets)) },
    { id: 'calendar.list.export', label: 'webmail.calendarUx.ics.export', icon: DownloadIcon, group: 'ics', visible: ({ targets }) => one(targets).kind === 'own' || one(targets).access === 'owner' || one(targets).access === 'editor' || one(targets).access === 'reviewer' && !/^(s|h|b)\./.test(one(targets).id), run: ({ targets }) => handlers.exportIcs(one(targets)) },
    { id: 'calendar.list.import', label: 'webmail.calendarUx.ics.importMenu', icon: UploadIcon, group: 'ics', visible: ({ targets }) => one(targets).canCreate, run: ({ targets }) => handlers.importIcs(one(targets)) },
    {
      id: 'calendar.list.delete',
      label: 'webmail.calendar.list.delete',
      icon: Trash2Icon,
      group: 'remove',
      danger: true,
      visible: ({ targets }) => isExtraCalendar(one(targets)),
      confirm: ({ targets }) => ({
        title: 'webmail.calendar.list.deleteTitle',
        description: { key: 'webmail.calendar.list.deleteDescription', params: { name: one(targets).name } },
        confirm: 'webmail.calendar.list.delete',
      }),
      run: ({ targets }) => handlers.remove(one(targets)),
    },
    {
      id: 'calendar.list.remove',
      label: 'webmail.calendar.list.remove',
      icon: XIcon,
      group: 'remove',
      visible: ({ targets }) => one(targets).kind !== 'own',
      run: ({ targets }) => handlers.remove(one(targets)),
    },
  ]);
}
